/* Ряд лиц «Кто в кадре» (BotPeople, APP-SPEC-5 §4.4) для «Фотосессии из селфи» (несколько, «Вы» всегда в кадре)
   и «Фотосессии товара» (режим «модель», один выбор). Подпись со счётом и «Изменить», строка «Вы, близкие и персонажи ·
   Персонажи ›», круги 56 px: «Вы», близкие (жёлтая точка — нет фото), «＋ Добавить», черта, персонажи, «Все ›»;
   под рядом — подпись-правило. Листы: «Люди», «Ваши фото», «Каким вы будете», профиль близкого, каталог персонажей. */
import { useState, type ReactNode } from 'react';
import type { Person, UserAvatar } from '../../../api/people';
import { uiFormat, uiText } from '../../../i18n';
import { peopleServer } from '../../profile/people-server';
import { artworkUrl } from '../../studio/studio-art-model';
import { StudioIcon } from '../../studio/StudioIcon';
import { StudioSheet } from '../../studio/StudioSheet';
import { PersonSheet, SelfPhotosSheet } from './PersonSheet';
import type { PeopleData } from './use-people-data';
import { selfFace, type Face } from './people-model';
import './people-picker.css';

const initial = (name: string) => Array.from(name.trim())[0] ?? '·';
function Circle({ face, on, onClick, locked }: { face: Face; on: boolean; onClick(): void; locked: boolean }) {
  const image = artworkUrl(face.image);
  return (
    <button
      type="button"
      className={`ps-fc${on ? ' on' : ''}${face.disabled ? ' off' : ''}`}
      aria-pressed={on}
      aria-label={face.sub ? `${face.name} · ${face.sub}` : face.name}
      disabled={locked}
      title={face.disabled ? uiText(face.disabled) : undefined}
      onClick={onClick}
    >
      <span className="ps-fo">
        <span className="ps-fi" aria-hidden="true">
          {image ? <img src={image} alt="" /> : face.icon ? <StudioIcon kind={face.icon} /> : <span className="ini">{initial(face.name)}</span>}
        </span>
        {face.needsPhoto && <i className="shk-need" aria-hidden="true" />}
        <i className="ck" aria-hidden="true">
          <StudioIcon kind="check" />
        </i>
      </span>
      <b>{face.name}</b>
      {face.sub && <small>{face.sub}</small>}
    </button>
  );
}

const personFace = (person: Person): Face => ({
  value: `person:${person.id}`,
  name: person.name,
  sub: person.photos.length ? person.rel : uiText('Добавьте фото'),
  image: person.photos[0]?.url,
  needsPhoto: !person.photos.length,
  disabled: person.selectable ? undefined : person.disabled_reason || 'Этот человек не участвует',
});

export function PeoplePicker({
  data,
  avatar,
  mode,
  selected,
  limit,
  locked,
  onToggle,
  extra = [],
  allowPets = true,
  withCharacters = true,
  caption,
  heading,
  children,
}: {
  data: PeopleData;
  avatar: UserAvatar | null;
  /** multi — несколько, «Вы» всегда в кадре; single — один выбор (модель) */
  mode: 'multi' | 'single';
  /** выбранные value: `me`, `person:<id>`, `char:<id>` и свои круги экрана (`none`, `gen`) */
  selected: string[];
  limit: number;
  locked: boolean;
  onToggle(value: string): void;
  /** свои круги экрана: «Без модели» первым, «Модель ИИ» после персонажей */
  extra?: Array<Face & { place: 'first' | 'last' }>;
  allowPets?: boolean;
  withCharacters?: boolean;
  caption: string;
  heading?: string;
  children?: ReactNode;
}) {
  const [sheet, setSheet] = useState<'list' | 'self' | 'variant' | 'chars' | { person: Person | null } | null>(null);
  const [query, setQuery] = useState('');
  const [hint, setHint] = useState('');
  const me = selfFace(data, avatar);
  const persons = data.persons.filter(person => allowPets || !person.is_pet);
  const characters = withCharacters ? data.characters : [];
  const inFrame = selected.filter(value => !['none', 'gen'].includes(value));
  const full = mode === 'multi' && inFrame.length >= limit;
  const pick = (face: Face) => {
    setHint('');
    if (face.disabled) return setHint(uiText(face.disabled));
    if (face.value === 'me' && !me.ready) return setSheet('self');
    if (face.value.startsWith('person:') && face.needsPhoto) {
      const person = persons.find(item => `person:${item.id}` === face.value);
      return setSheet({ person: person ?? null });
    }
    if (mode === 'multi' && face.value === 'me') return setHint(uiText('«Вы» в кадре всегда'));
    if (mode === 'multi' && !selected.includes(face.value) && full)
      return setHint(uiFormat('В кадре уже {0} из {0}: снимите отметку с кого-нибудь', limit));
    onToggle(face.value);
  };
  const charFaces: Face[] = characters.map(card => ({ value: `char:${card.id}`, name: card.name, image: card.image }));
  /* как rowChList шаблона (assets/bot-people.js): шесть первых персонажей на своих местах, выбранный из каталога, которого среди них нет, — впереди;
     нажатый в ряду персонаж не переезжает */
  const firstChars = charFaces.slice(0, 6);
  const shownChars = [...selected.flatMap(value => charFaces.filter(face => face.value === value && !firstChars.includes(face))), ...firstChars];
  const count =
    mode === 'multi'
      ? !me.ready
        ? uiText('нужно: Вы')
        : uiFormat('{0} из {1}', inFrame.length, limit)
      : selected[0] && selected[0] !== 'none'
        ? uiText('1 из 1')
        : uiText('необязательно');
  const filtered = charFaces.filter(face => face.name.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()));
  return (
    <section className="ps-face shk-people" aria-label={uiText(heading ?? 'Кто в кадре')}>
      <div className="glbl shk-people-h">
        <span>
          {uiText(heading ?? 'Кто в кадре')} · {count}
        </span>
        <button type="button" className="lnk shk-edit" disabled={locked} onClick={() => setSheet('list')}>
          {uiText('Изменить')}
        </button>
      </div>
      {withCharacters && (
        <div className="shk-people-sub">
          <span>{uiText(mode === 'multi' ? 'Вы, близкие и персонажи' : 'Вы, близкий или персонаж')}</span>
          <button type="button" className="lnk" disabled={locked || !characters.length} onClick={() => setSheet('chars')}>
            {uiText('Персонажи')} ›
          </button>
        </div>
      )}
      {data.loading && !data.self && <p role="status">{uiText('Загружаем…')}</p>}
      {data.error && (
        <p role="alert">
          {uiText('Не удалось загрузить людей.')}{' '}
          <button type="button" onClick={data.reload}>
            {uiText('Повторить')}
          </button>
        </p>
      )}
      <div className="ps-fr nosb" role="group" aria-label={uiText('Люди и персонажи')}>
        {extra.filter(face => face.place === 'first').map(face => (
          <Circle key={face.value} face={face} on={selected.includes(face.value)} locked={locked} onClick={() => pick(face)} />
        ))}
        <span className="shk-me">
          <Circle face={me} on={mode === 'multi' ? me.ready : selected.includes('me')} locked={locked} onClick={() => pick(me)} />
          {(data.variants.length > 0 || data.self?.src === 'avatar') && (
            <button type="button" className="shk-var" aria-label={uiText('Каким вы будете')} disabled={locked} onClick={() => setSheet('variant')}>
              <StudioIcon kind="sort" />
            </button>
          )}
        </span>
        {persons.map(person => {
          const face = personFace(person);
          return <Circle key={face.value} face={face} on={selected.includes(face.value)} locked={locked} onClick={() => pick(face)} />;
        })}
        <button type="button" className="ps-fc add" disabled={locked} onClick={() => setSheet({ person: null })}>
          <span className="ps-fo">
            <span className="ps-fi" aria-hidden="true">
              <StudioIcon kind="plus" />
            </span>
          </span>
          <b>{uiText('Добавить')}</b>
        </button>
        {!!shownChars.length && <span className="shk-sep" aria-hidden="true" />}
        {shownChars.map(face => (
          <Circle key={face.value} face={face} on={selected.includes(face.value)} locked={locked} onClick={() => pick(face)} />
        ))}
        {extra.filter(face => face.place === 'last').map(face => (
          <Circle key={face.value} face={face} on={selected.includes(face.value)} locked={locked} onClick={() => pick(face)} />
        ))}
        {charFaces.length > shownChars.length && (
          <button type="button" className="ps-fc more" disabled={locked} onClick={() => setSheet('chars')}>
            <span className="ps-fo">…</span>
            <b>{uiText('Все')} ›</b>
          </button>
        )}
      </div>
      {hint && (
        <p className="shk-hint" role="status">
          {hint}
        </p>
      )}
      {children}
      <p className="bc-cap shk-cap">
        <StudioIcon kind="eye" />
        {uiText(caption)}
        {!me.ready && mode === 'multi' ? ` · ${uiText('Ваши фото: один раз для всех инструментов')}` : ''}
      </p>
      {sheet === 'list' && (
        <StudioSheet title="Люди" onClose={() => setSheet(null)}>
          <h2>{uiText('Люди')}</h2>
          <p>{mode === 'multi' ? uiFormat('Отметьте, кто будет в кадре · до {0} человек', limit) : uiText('Выберите, кто будет моделью')}</p>
          <div className="shk-list">
            {[me as Face, ...persons.map(personFace)].map(face => {
              const on = face.value === 'me' ? (mode === 'multi' ? me.ready : selected.includes('me')) : selected.includes(face.value);
              const person = persons.find(item => `person:${item.id}` === face.value);
              return (
                <div className={`shk-row${face.disabled || face.needsPhoto ? ' off' : ''}`} key={face.value}>
                  <span className="ps-fi" aria-hidden="true">
                    {artworkUrl(face.image) ? <img src={artworkUrl(face.image)!} alt="" /> : <span className="ini">{initial(face.name)}</span>}
                  </span>
                  <span className="m">
                    <b>{face.name}</b>
                    <small className={face.needsPhoto ? 'warn' : ''}>
                      {face.value === 'me'
                        ? me.sub
                          ? uiFormat('вариант «{0}»', me.sub)
                          : uiFormat('{0} фото', data.self?.photos.length ?? 0)
                        : person && person.photos.length
                          ? `${person.rel} · ${uiFormat('{0} фото', person.photos.length)}`
                          : uiText('нет фото')}
                    </small>
                  </span>
                  <button
                    type="button"
                    className="iconb"
                    aria-label={uiFormat('Изменить: {0}', face.name)}
                    disabled={locked}
                    onClick={() => setSheet(face.value === 'me' ? 'self' : { person: person ?? null })}
                  >
                    <StudioIcon kind="edit" />
                  </button>
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={on}
                    aria-label={uiFormat('В кадре: {0}', face.name)}
                    className={`shk-tick${on ? ' on' : ''}`}
                    disabled={locked}
                    onClick={() => pick(face)}
                  >
                    {on && <StudioIcon kind="check" />}
                  </button>
                </div>
              );
            })}
          </div>
          {hint && <p role="status">{hint}</p>}
          <button type="button" className="btn line dash" disabled={locked} onClick={() => setSheet({ person: null })}>
            {uiText('Добавить близкого')}
          </button>
          <button type="button" className="btn" onClick={() => setSheet(null)}>
            {uiText('Готово')}
          </button>
        </StudioSheet>
      )}
      {sheet === 'self' && <SelfPhotosSheet self={data.self} onClose={() => setSheet(null)} onSaved={() => setSheet(null)} />}
      {sheet === 'variant' && (
        <StudioSheet title="Каким вы будете" onClose={() => setSheet(null)}>
          <h2>{uiText('Каким вы будете')}</h2>
          <p>{uiText('Выбор встанет во все инструменты')}</p>
          <div className="shk-list">
            {[
              { id: 'photos', name: uiText('Мои фото'), sub: uiFormat('{0} фото · как в жизни', data.self?.photos.length ?? 0), image: data.self?.photos[0]?.url },
              ...data.variants
                .filter(item => item.status === 'ready')
                .map(item => ({ id: String(item.id), name: `${uiText('Вы')} · ${item.variant_name}`, sub: '', image: item.image_url })),
            ].map(item => {
              const on = item.id === 'photos' ? data.self?.src !== 'avatar' : String(data.self?.pick) === item.id && data.self?.src === 'avatar';
              return (
                <button
                  type="button"
                  key={item.id}
                  className={`shk-row${on ? ' on' : ''}`}
                  aria-pressed={on}
                  disabled={locked}
                  onClick={async () => {
                    try {
                      await peopleServer.self({ pick: item.id === 'photos' ? 'photos' : Number(item.id) });
                      document.dispatchEvent(new CustomEvent('bot:me'));
                      setSheet(null);
                    } catch (failure) {
                      setHint(uiText(peopleServer.error(failure)));
                    }
                  }}
                >
                  <span className="ps-fi" aria-hidden="true">
                    {artworkUrl(item.image) ? <img src={artworkUrl(item.image)!} alt="" /> : <span className="ini">{initial(item.name)}</span>}
                  </span>
                  <span className="m">
                    <b>{item.name}</b>
                    {item.sub && <small>{item.sub}</small>}
                  </span>
                </button>
              );
            })}
          </div>
          {hint && <p role="status">{hint}</p>}
        </StudioSheet>
      )}
      {sheet && typeof sheet === 'object' && (
        <PersonSheet
          person={sheet.person}
          allowPets={allowPets}
          onClose={() => setSheet(null)}
          onSaved={saved => {
            setSheet(null);
            if (!sheet.person && (mode === 'single' || !full)) onToggle(`person:${saved.id}`);
          }}
        />
      )}
      {sheet === 'chars' && (
        <StudioSheet title="Персонажи" onClose={() => setSheet(null)}>
          <div className="shk-chars-h">
            <h2>{uiText(heading ?? 'Кто в кадре')}</h2>
            <span>{mode === 'multi' ? uiFormat('{0} из {1}', inFrame.length, limit) : uiText('один выбор')}</span>
          </div>
          <label className="search">
            <input
              type="search"
              value={query}
              placeholder={uiFormat('Искать среди {0} персонажей', charFaces.length)}
              aria-label={uiText('Искать персонажа')}
              onChange={event => setQuery(event.target.value)}
            />
          </label>
          <div className="studio-native-person-grid">
            {filtered.map(face => (
              <Circle
                key={face.value}
                face={!selected.includes(face.value) && full ? { ...face, disabled: uiFormat('В кадре уже {0} из {0}: снимите отметку с кого-нибудь', limit) } : face}
                on={selected.includes(face.value)}
                locked={locked}
                onClick={() => {
                  pick(!selected.includes(face.value) && full ? { ...face, disabled: uiFormat('В кадре уже {0} из {0}: снимите отметку с кого-нибудь', limit) } : face);
                  if (selected.includes(face.value) || !full) setSheet(null);
                }}
              />
            ))}
          </div>
          {!filtered.length && <p role="status">{uiText('Ничего не нашлось · Попробуйте другое слово')}</p>}
          {hint && <p role="status">{hint}</p>}
        </StudioSheet>
      )}
    </section>
  );
}
